import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import { ListPagination } from '@/components/admin/shared/ListPagination';
import { ListSearchInput } from '@/components/admin/shared/ListSearchInput';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ConfirmDialog } from '@/components/ui/confirmDialog';
import { SEARCH_DEBOUNCE_MILLISECONDS } from '@/constants/adminPagination';
import { ASSISTANT_CONVERSATION_PAGE_SIZE } from '@/constants/assistant';
import {
  useAssistantConversationsQuery,
  useDeleteAssistantConversationMutation,
} from '@/hooks/useAssistant';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { formatIsoDateTimeToTimestamp } from '@/utils/dateFormatting';
import { useAssistantPanel } from './assistantPanelContext';
import { showErrorNotification } from '@/utils/notifications';

export function AssistantHistoryView() {
  const { t } = useTranslation('assistant');
  const { selectConversation, setView, conversationId } = useAssistantPanel();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [conversationToDelete, setConversationToDelete] = useState<string | null>(null);
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MILLISECONDS);
  const conversationsQuery = useAssistantConversationsQuery(page, debouncedSearch, true);
  const deleteMutation = useDeleteAssistantConversationMutation();
  const conversations = conversationsQuery.data === undefined ? [] : conversationsQuery.data.items;
  const totalItems = conversationsQuery.data === undefined ? 0 : conversationsQuery.data.totalItems;

  return (
    <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3">
      <button
        type="button"
        onClick={function backToChat() {
          setView('chat');
        }}
        className="text-xs font-semibold text-zooprimary flex items-center gap-1 mb-3"
      >
        <Icon name="ArrowLeft" />
        {t('history.back')}
      </button>
      <ListSearchInput
        value={search}
        onChange={function updateSearch(nextValue) {
          setSearch(nextValue);
          setPage(1);
        }}
        placeholder={t('history.searchPlaceholder')}
      />
      {conversationsQuery.isLoading ? (
        <div className="py-10 flex justify-center">
          <LoadingSpinner className="w-6 h-6" />
        </div>
      ) : conversations.length === 0 ? (
        <p className="text-sm text-zoomuted mt-6 text-center">
          {debouncedSearch.trim().length > 0 ? t('history.noMatch') : t('history.empty')}
        </p>
      ) : (
        <ul className="mt-3 space-y-1">
          {conversations.map(function renderConversation(conversation) {
            const isActive = conversation.id === conversationId;
            return (
              <li key={conversation.id} className="group flex items-center gap-2">
                <button
                  type="button"
                  onClick={function open() {
                    selectConversation(conversation.id);
                  }}
                  className={
                    isActive
                      ? 'flex-1 min-w-0 text-left px-3 py-2.5 rounded-xl bg-zoovpalelavender'
                      : 'flex-1 min-w-0 text-left px-3 py-2.5 rounded-xl hover:bg-zoovpalelavender'
                  }
                >
                  <span className="block text-sm text-zooindigo truncate">
                    {conversation.title.length > 0 ? conversation.title : t('history.untitled')}
                  </span>
                  <span className="block text-[11px] text-zoomuted">
                    {formatIsoDateTimeToTimestamp(conversation.lastMessageAt)}
                  </span>
                </button>
                <button
                  type="button"
                  aria-label={t('history.delete')}
                  title={t('history.delete')}
                  onClick={function confirmDelete() {
                    setConversationToDelete(conversation.id);
                  }}
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-zoomuted hover:bg-zooerrorbg hover:text-zooerrortext"
                >
                  <Icon name="Trash" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <div className="mt-3">
        <ListPagination
          page={page}
          pageSize={ASSISTANT_CONVERSATION_PAGE_SIZE}
          totalItems={totalItems}
          onPageChange={setPage}
        />
      </div>
      <p className="text-xs text-zoomuted mt-6 flex items-start gap-2">
        <Icon name="Lock" className="shrink-0 mt-0.5" />
        {t('empty.privacy')}
      </p>
      {conversationToDelete === null ? null : (
        <ConfirmDialog
          title={t('history.deleteTitle')}
          description={t('history.deleteDescription')}
          confirmLabel={t('history.delete')}
          tone="danger"
          iconName="Trash"
          isConfirming={deleteMutation.isPending}
          onCancel={function cancel() {
            setConversationToDelete(null);
          }}
          onConfirm={function remove() {
            const deletedId = conversationToDelete;
            deleteMutation.mutate(deletedId, {
              onSuccess: function afterDelete() {
                if (deletedId === conversationId) {
                  selectConversation(null);
                  setView('history');
                }
                setConversationToDelete(null);
              },
              onError: function explainFailure(deleteError) {
                const backendMessage = resolveApiErrorMessage(deleteError);
                showErrorNotification(
                  backendMessage.length > 0 ? backendMessage : t('errors.generic')
                );
                setConversationToDelete(null);
              },
            });
          }}
        />
      )}
    </div>
  );
}
